import { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Briefcase,
  ClipboardList,
  Coins,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  CalendarDays,
  LayoutDashboard,
  BarChart3,
  Menu,
  Wallet,
  Receipt,
  Contact,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ssoOpening, setSsoOpening] = useState(false);

  const handleOpenGpProje = async (e) => {
    if (e) e.preventDefault();
    if (ssoOpening) return;
    setSsoOpening(true);

    // Open blank window synchronously within user gesture so mobile/desktop popup blockers allow it
    const newTab = window.open("about:blank", "_blank");
    if (newTab) {
      try {
        newTab.opener = null;
      } catch (_) {}
    }

    try {
      const r = await api.post("/auth/sso/gp-proje-ticket");
      const redirectUrl = r.data?.redirect_url;
      if (!redirectUrl) {
        throw new Error("GP Proje yönlendirme adresi alınamadı.");
      }
      if (newTab && !newTab.closed) {
        newTab.location.replace(redirectUrl);
      } else {
        window.location.assign(redirectUrl);
      }
    } catch (err) {
      if (newTab && !newTab.closed) {
        try {
          newTab.close();
        } catch (_) {}
      }
      toast.error(formatApiError(err, "GP Proje açılamadı."));
    } finally {
      setSsoOpening(false);
    }
  };

  const navItems = useMemo(
    () => [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
      { to: "/musteriler", label: "Müşteriler", icon: Contact, testId: "nav-customers" },
      { to: "/isler", label: "İşler", icon: Briefcase, testId: "nav-jobs" },
      { to: "/siparisler", label: "Siparişler", icon: ClipboardList, testId: "nav-orders" },
      { to: "/operasyon/kanban", label: "Kanban", icon: LayoutGrid, testId: "nav-kanban" },
      { to: "/operasyon/takvim", label: "Takvim", icon: CalendarDays, testId: "nav-calendar" },
      { to: "/maliyetler", label: "İş Maliyetleri", icon: Coins, testId: "nav-costs" },
      { to: "/tahsilatlar", label: "Tahsilatlar", icon: Wallet, testId: "nav-collections" },
      { to: "/raporlar", label: "Raporlar", icon: BarChart3, testId: "nav-reports", end: true },
      { to: "/raporlar/alacaklar", label: "Alacaklar Raporu", icon: Receipt, testId: "nav-receivables-report", sub: true },
      hasRole(user, "admin")
        ? { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, testId: "nav-users" }
        : null,
      hasRole(user, "admin")
        ? { to: "/ayarlar", label: "Ayarlar", icon: Settings, testId: "nav-settings" }
        : null,
    ].filter(Boolean),
    [user]
  );

  const handleLogout = async () => {
    await logout();
    navigate("/giris");
  };

  return (
    <div className="flex h-screen w-full bg-[#F4F6F8] text-[#171A1F]">
      {/* Sidebar */}
      <aside
        className={`gp-sidebar hidden md:flex flex-col bg-[#171A1F] text-slate-100 border-r border-[#2A2F38] ${collapsed ? "w-16" : "w-64"}`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-3 border-b border-[#2A2F38]">
          {!collapsed ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <img
                src="/gungorpen_logo_nobg.png"
                alt="Güngör Pen"
                className="h-8 w-auto object-contain shrink-0"
              />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 border-l border-slate-700 pl-2 leading-tight truncate shrink-0">
                GP Yönetim
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full overflow-hidden">
              <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                <img
                  src="/gungorpen_logo_nobg.png"
                  alt="Güngör Pen"
                  className="h-7 max-w-none"
                  style={{ objectFit: "cover", objectPosition: "left center", width: "28px" }}
                />
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded hover:bg-[#2A2F38] shrink-0"
            aria-label="Menüyü daralt"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `gp-nav-item ${it.sub && !collapsed ? "!pl-9 !text-[13px]" : ""} ${isActive ? "!bg-[#FF353A] !text-white font-medium shadow-sm" : ""}`
                }
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span className="truncate">{it.label}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-2 pb-2 pt-1 border-t border-[#2A2F38]">
          {!collapsed ? (
            <button
              type="button"
              onClick={handleOpenGpProje}
              disabled={ssoOpening}
              data-testid="sidebar-gp-proje-card"
              className="group w-full flex items-center gap-3 p-2.5 rounded-lg bg-[#21252D] hover:bg-[#2A2F38] border border-[#2F3542] hover:border-[#0088CC]/60 transition-all text-left shadow-sm disabled:opacity-60"
              title="GP Proje uygulamasını yeni sekmede aç"
            >
              <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center p-1 shrink-0 shadow-inner">
                <img
                  src="/gp_proje_logo.png"
                  alt="GP Proje"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white group-hover:text-[#5BB8E8] transition-colors truncate">
                  GP Proje
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  Proje &amp; Maliyet Takibi
                </div>
              </div>
              {ssoOpening ? (
                <Loader2 size={15} className="text-slate-400 animate-spin shrink-0" />
              ) : (
                <ExternalLink
                  size={15}
                  className="text-slate-400 group-hover:text-white transition-colors shrink-0"
                />
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenGpProje}
              disabled={ssoOpening}
              data-testid="sidebar-gp-proje-collapsed"
              aria-label="GP Proje uygulamasını yeni sekmede aç"
              title="GP Proje – Proje & Maliyet Takibi (Yeni sekmede aç)"
              className="w-full flex items-center justify-center p-1.5 rounded-lg bg-[#21252D] hover:bg-[#2A2F38] border border-[#2F3542] hover:border-[#0088CC]/60 transition-all disabled:opacity-60"
            >
              <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center p-1 shrink-0">
                {ssoOpening ? (
                  <Loader2 size={16} className="text-[#0088CC] animate-spin" />
                ) : (
                  <img
                    src="/gp_proje_logo.png"
                    alt="GP Proje"
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            </button>
          )}
        </div>
        <div className="p-3 text-[11px] text-slate-500 border-t border-[#2A2F38]">
          {!collapsed && <span>v0.1.0 · Güngör Pen</span>}
        </div>
      </aside>

      {/* Mobile Drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="bg-[#171A1F] text-slate-100 border-r border-[#2A2F38] p-0 w-72 flex flex-col justify-between"
          data-testid="mobile-nav-drawer"
        >
          <div>
            <SheetHeader className="p-4 border-b border-[#2A2F38] text-left">
              <div className="flex items-center gap-3">
                <img
                  src="/gungorpen_logo_nobg.png"
                  alt="Güngör Pen"
                  className="h-8 w-auto object-contain shrink-0"
                />
                <div>
                  <SheetTitle className="text-sm font-bold text-white leading-tight">
                    Güngör Pen
                  </SheetTitle>
                  <SheetDescription className="text-[11px] font-semibold text-slate-400">
                    GP Yönetim
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            {/* User profile info in mobile drawer */}
            <div className="p-4 bg-[#21252D] border-b border-[#2A2F38]">
              <div className="text-sm font-semibold text-white">{user?.full_name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="h-2 w-2 rounded-full bg-[#168DEB]" />
                <span className="text-xs text-slate-300">
                  {ROLE_LABELS[user?.role] || user?.role}
                </span>
              </div>
            </div>

            {/* Navigation links */}
            <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-300px)]">
              {navItems.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`mobile-${it.testId}`}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 ${it.sub ? "pl-8 pr-3" : "px-3"} py-2.5 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? "!bg-[#FF353A] !text-white font-semibold shadow-sm"
                          : "text-slate-300 hover:bg-[#2A2F38] hover:text-white"
                      }`
                    }
                  >
                    <Icon size={18} className="shrink-0" />
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div className="p-4 border-t border-[#2A2F38] space-y-3">
            <button
              type="button"
              onClick={(e) => {
                setMobileOpen(false);
                handleOpenGpProje(e);
              }}
              disabled={ssoOpening}
              data-testid="mobile-gp-proje-card"
              className="group w-full flex items-center gap-3 p-2.5 rounded-lg bg-[#21252D] hover:bg-[#2A2F38] border border-[#2F3542] hover:border-[#0088CC]/60 transition-all text-left shadow-sm disabled:opacity-60 min-h-[52px]"
            >
              <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center p-1 shrink-0 shadow-inner">
                <img
                  src="/gp_proje_logo.png"
                  alt="GP Proje"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white group-hover:text-[#5BB8E8] transition-colors truncate">
                  GP Proje
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  Proje &amp; Maliyet Takibi
                </div>
              </div>
              {ssoOpening ? (
                <Loader2 size={15} className="text-slate-400 animate-spin shrink-0" />
              ) : (
                <ExternalLink
                  size={15}
                  className="text-slate-400 group-hover:text-white transition-colors shrink-0"
                />
              )}
            </button>
            <Button
              variant="ghost"
              onClick={() => {
                setMobileOpen(false);
                handleLogout();
              }}
              data-testid="mobile-logout-button"
              className="w-full justify-start text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 p-2.5 text-sm h-auto"
            >
              <LogOut size={16} className="mr-2 shrink-0" /> Çıkış Yap
            </Button>
            <div className="text-[11px] text-slate-500">v0.1.0 · Güngör Pen Mobil</div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex flex-1 flex-col min-w-0">
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 bg-white border-b border-[#DCE2E8] shrink-0 min-w-0">
          {/* Mobile header left */}
          <div className="flex items-center gap-2 md:hidden min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="p-2 text-slate-700 hover:bg-[#F4F6F8] rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
              aria-label="Mobil Menüyü Aç"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
              <img
                src="/gungorpen_logo_nobg.png"
                alt="Güngör Pen"
                className="h-7 w-auto object-contain shrink-0"
              />
              <span className="text-xs font-bold text-[#171A1F] truncate shrink-0">
                GP Yönetim
              </span>
            </div>
          </div>

          {/* Desktop header left */}
          <div className="hidden md:flex items-center gap-3 min-w-0">
            <span className="text-sm font-semibold text-[#171A1F]">Güngör Pen</span>
            <Separator orientation="vertical" className="h-4 bg-[#DCE2E8]" />
            <span className="text-xs font-medium text-slate-500">GP Yönetim</span>
            <Separator orientation="vertical" className="h-4 bg-[#DCE2E8]" />
            <span className="text-sm font-medium text-slate-800" data-testid="header-active-user">
              {user?.full_name}
            </span>
          </div>

          {/* Header right user menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-[#DCE2E8] text-slate-700 hover:bg-[#F4F6F8] min-h-[44px] sm:min-h-0 px-3"
                data-testid="user-menu-trigger"
              >
                <span className="h-2 w-2 rounded-full bg-[#168DEB] shrink-0" />
                <span className="hidden sm:inline ml-2">{ROLE_LABELS[user?.role] || user?.role}</span>
                <span className="sm:hidden font-semibold ml-1.5 text-xs">
                  {user?.full_name?.split(" ")[0]}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-[#DCE2E8]">
              <DropdownMenuLabel>
                <div className="text-sm font-medium text-[#171A1F]">{user?.full_name}</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
                <div className="text-[11px] font-semibold text-[#168DEB] mt-0.5 sm:hidden">
                  {ROLE_LABELS[user?.role] || user?.role}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#DCE2E8]" />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer min-h-[44px] sm:min-h-0"
              >
                <LogOut size={14} className="mr-2" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-auto bg-[#F4F6F8]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
